import {
    BOOTSTRAP_ICONS,
    BRAIN_GAME_ICON_BY_ID,
    BRAIN_GAME_ICON_IMAGE_BY_ID,
    GAME_ROUTE_PREFIX,
} from './shared';
import MemoryMatchGame from './games/MemoryMatchGame';
import MemoryMatrixGame from './games/MemoryMatrixGame';
import SequenceRepeatGame from './games/SequenceRepeatGame';
import SymbolNBackGame from './games/SymbolNBackGame';
import StroopGame from './games/StroopGame';
import SchulteTableGame from './games/SchulteTableGame';
import VisualSearchGame from './games/VisualSearchGame';
import FlankerDirectionGame from './games/FlankerDirectionGame';
import ChanceCardsGame from './games/ChanceCardsGame';
import TurnMatchGame from './games/TurnMatchGame';
import NumberRainGame from './games/NumberRainGame';
import OrderRushGame from './games/OrderRushGame';
import EstimateDuelGame from './games/EstimateDuelGame';
import NewCatchGame from './games/NewCatchGame';
import GoNoGoGame from './games/GoNoGoGame';
import TrailMakingGame from './games/TrailMakingGame';
import ObjectShelfGame from './games/ObjectShelfGame';
import BalanceBeamGame from './games/BalanceBeamGame';
import PercentPopGame from './games/PercentPopGame';
import { DISTINCT_GAME_DEFINITIONS } from './distinctGameRegistry';
import { getLocalizedBrainGame } from '../../i18n/brainGameLocales';
import brainGameLevels from '../../lib/brainGameLevels';

const { BRAIN_GAME_LEVEL_CONFIG } = brainGameLevels;

const GAME_DEFINITION_BASES = [
    {
        id: 'memory-match',
        hud: {score: true},
        title: 'Memory Match',
        shortTitle: 'Pairs',
        summary: 'Flip two cards, remember their positions, and clear every pair with as few moves as possible.',
        focus: 'Classic card memory',
        action: 'Clear pairs',
        Component: MemoryMatchGame,
    },
    {
        id: 'memory-matrix',
        hud: {streak: true},
        title: 'Grid Memory',
        shortTitle: 'Grid',
        summary: 'Memorize highlighted cells, then tap the same cells in any order.',
        focus: 'Spatial recall',
        action: 'Recall cells',
        tutorialVersion: 'path-grid',
        Component: MemoryMatrixGame,
    },
    {
        id: 'sequence-repeat',
        hud: {score: true},
        title: 'Sequence Repeat',
        shortTitle: 'Sequence',
        summary: 'Follow an expanding button sequence, then repeat it exactly.',
        focus: 'Working memory',
        action: 'Repeat order',
        Component: SequenceRepeatGame,
    },
    {
        id: 'symbol-n-back',
        hud: {score: true,streak: true},
        title: 'Symbol N-Back',
        shortTitle: 'N-Back',
        summary: 'Decide whether each symbol matches the one shown the indicated number of cards ago.',
        focus: 'Updating memory',
        action: 'Compare',
        Component: SymbolNBackGame,
    },
    {
        id: 'stroop-colors',
        hud: {score: true,streak: true},
        title: 'Stroop Colors',
        shortTitle: 'Stroop',
        summary: 'Read the word while ignoring the ink color, then tap the matching color.',
        focus: 'Inhibition',
        action: 'Choose word',
        Component: StroopGame,
    },
    {
        id: 'schulte-table',
        hud: {elapsed: true,mistakes: true},
        title: 'Schulte Table',
        shortTitle: 'Schulte',
        summary: 'Find every number in order while the timer runs.',
        focus: 'Scanning speed',
        action: 'Find numbers',
        Component: SchulteTableGame,
    },
    {
        id: 'visual-search',
        hud: {score: true,streak: true},
        title: 'Visual Search',
        shortTitle: 'Search',
        summary: 'Pick the target symbol from a busy field of similar distractors.',
        focus: 'Selective attention',
        action: 'Find target',
        Component: VisualSearchGame,
    },
    {
        id: 'flanker-direction',
        hud: {score: true,streak: true},
        title: 'Flanker Direction',
        shortTitle: 'Flanker',
        summary: 'Read the middle arrow while ignoring conflicting side arrows.',
        focus: 'Selective inhibition',
        action: 'Pick direction',
        Component: FlankerDirectionGame,
    },
    {
        id: 'go-no-go',
        hud: {timer: true,score: true,streak: true},
        title: 'Go / No-Go',
        shortTitle: 'Go No-Go',
        summary: 'Press when an Ace or King appears and do nothing for every other card.',
        focus: 'Impulse control',
        action: 'Press or hold',
        Component: GoNoGoGame,
    },
    {
        id: 'trail-making',
        hud: {elapsed: true,mistakes: true},
        title: 'Trail Making',
        shortTitle: 'Trails',
        summary: 'Connect numbers and letters in alternating order without losing the path.',
        focus: 'Mental flexibility',
        action: 'Connect path',
        Component: TrailMakingGame,
    },
    {
        id: 'chance-cards',
        hud: {score: true,streak: true},
        title: 'Number Odds',
        shortTitle: 'Odds',
        summary: 'Compare two numeric odds and choose the stronger probability.',
        focus: 'Probabilistic reasoning',
        action: 'Compare numbers',
        Component: ChanceCardsGame,
    },
    {
        id: 'turn-match',
        hud: {score: true,streak: true},
        title: 'Turn Match',
        shortTitle: 'Turn',
        summary: 'Mentally rotate a cube pattern and find the same structure from a new angle.',
        focus: 'Mental rotation',
        action: 'Rotate in mind',
        Component: TurnMatchGame,
    },
    {
        id: 'number-rain',
        hud: {timer: true,score: true,streak: true},
        title: 'Number Rain',
        shortTitle: 'Rain',
        summary: 'Solve quick arithmetic drops before the next one appears.',
        focus: 'Calculation speed',
        action: 'Solve fast',
        Component: NumberRainGame,
    },
    {
        id: 'order-rush',
        hud: {timer: true,score: true,streak: true},
        title: 'Order Rush',
        shortTitle: 'Orders',
        summary: 'Memorize a short order, then rebuild it from the tray.',
        focus: 'Working memory',
        action: 'Fill orders',
        Component: OrderRushGame,
    },
    {
        id: 'estimate-duel',
        hud: {score: true,streak: true},
        title: 'Estimate Duel',
        shortTitle: 'Estimate',
        summary: 'Compare two dot groups and pick the larger quantity without counting.',
        focus: 'Number sense',
        action: 'Estimate more',
        Component: EstimateDuelGame,
    },
    {
        id: 'new-catch',
        hud: {timer: true,score: true,streak: true},
        title: 'New Catch',
        shortTitle: 'Catch',
        summary: 'Watch a small board, notice the changed tile, and tap it fast.',
        focus: 'Change detection',
        action: 'Catch change',
        Component: NewCatchGame,
    },
    {
        id: 'object-shelf',
        title: 'Object Shelf',
        shortTitle: 'Shelf',
        focus: 'Recognition memory',
        summary: 'Study a shelf, then identify an object that appeared.',
        action: 'Recall objects',
        icon: 'puzzle',
        group: 'other',
        plain: true,
        hud: { score: true, streak: true },
        tutorial: ['Study the object cards.', 'Wait for the cards to turn.', 'Choose one object you saw.'],
        Component: ObjectShelfGame,
    },
    {
        id: 'balance-beam',
        title: 'Balance Beam',
        shortTitle: 'Balance',
        focus: 'Equation reasoning',
        summary: 'Find the missing weight that keeps both sides equal.',
        action: 'Balance weights',
        icon: 'numbers',
        group: 'other',
        plain: true,
        hud: { score: true, streak: true },
        tutorial: ['Find the missing weight.', 'The unknown can be on either pan.', 'Positive and negative values must balance.'],
        Component: BalanceBeamGame,
    },
    {
        id: 'percent-pop',
        title: 'Percent Pop',
        shortTitle: 'Percent',
        focus: 'Proportional reasoning',
        summary: 'Find a percentage of a visible group.',
        action: 'Find the part',
        icon: 'pie',
        group: 'other',
        plain: true,
        hud: { timer: true, score: true, streak: true },
        tutorial: ['Read the percentage.', 'Count the full dot group.', 'Choose how many dots make that part.'],
        Component: PercentPopGame,
    },
    ...DISTINCT_GAME_DEFINITIONS,
];

export const GAME_DEFINITIONS = GAME_DEFINITION_BASES.map((definition) => ({
    ...definition,
    ...(BRAIN_GAME_LEVEL_CONFIG[definition.id] || {}),
}));

export const GAME_BY_ID = GAME_DEFINITIONS.reduce((items, game) => {
    items[game.id] = game;
    return items;
}, {});

export const getBrainTrainingGameFromHash = (hash = '') => {
    const normalizedHash = `${hash || ''}`.trim();

    if (!normalizedHash.startsWith(GAME_ROUTE_PREFIX)) {
        return null;
    }

    const rawId = normalizedHash.slice(GAME_ROUTE_PREFIX.length).replace(/^\/+/, '').split(/[?#]/)[0];

    return rawId && GAME_BY_ID[rawId] ? rawId : null;
};

const getGameHref = (gameId = '') => `${GAME_ROUTE_PREFIX}${gameId ? `/${gameId}` : ''}`;
const GAME_PREVIEW_ASSET_VERSION = '20260821-centered-crops-v3';
const getGamePreviewVideoSrc = (gameId = '') => (
    gameId ? `/assets/ufov/brain-games/previews/${gameId}.mp4?v=${GAME_PREVIEW_ASSET_VERSION}` : ''
);
const getGamePreviewPosterSrc = (gameId = '') => (
    gameId ? `/assets/ufov/brain-games/previews/${gameId}-poster.webp?v=${GAME_PREVIEW_ASSET_VERSION}` : ''
);

export const getBrainTrainingGameMenuItems = (language = 'en') => GAME_DEFINITIONS.map((definition) => {
    const {
        action,
        defaultLevel,
        id,
        maxLevel,
        minLevel,
        summary,
        title,
        shortTitle,
        focus,
        group,
        icon: iconKey,
    } = getLocalizedBrainGame(definition, language);

    return {
        action,
        defaultLevel,
        id,
        maxLevel,
        minLevel,
        summary,
        title,
        shortTitle,
        focus,
        group: group || 'other',
        icon: BOOTSTRAP_ICONS[iconKey || BRAIN_GAME_ICON_BY_ID[id]] || BOOTSTRAP_ICONS.puzzle,
        iconImageSrc: BRAIN_GAME_ICON_IMAGE_BY_ID[id] || '',
        href: getGameHref(id),
        previewOrientation: id === 'dual-track' ? 'landscape' : 'portrait',
        previewPosterSrc: getGamePreviewPosterSrc(id),
        previewVideoSrc: getGamePreviewVideoSrc(id),
    };
});

export const BRAIN_TRAINING_GAME_MENU_ITEMS = getBrainTrainingGameMenuItems('en');

export const getBrainTrainingGameHref = getGameHref;
